import {Component, EventEmitter, Input, OnInit, OnDestroy, Output} from '@angular/core';
import { custTile, TvdImpactCategory, TvdValuePillar, ValuePillarToImpactCategoryMapping } from '../../types/tvd-types';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { TVDService } from 'src/app/features/total-value-delivery/services/tvd.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { eroiService } from '../../services/eroi-metric.service';
import {eroiIds} from '../../types/tvd-types';
import { Subscription } from 'rxjs';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';

@Component({
    selector: 'custom-tile-entry-panel',
    templateUrl: './custom-tile-entry-panel.component.html',
    styleUrls: ['./custom-tile-entry-panel.component.scss'],
    standalone: false
})

export class CustomTileEntryPanelComponent implements OnInit, OnDestroy {
  private currencySubscription: Subscription | undefined;

  inputTitle: string = "";
  inputCost: string = "";
  inputValue: string = "";
  inputUnit: string = "";
  inputValuePillar: string = "";
  inputImpactCategory: string = "";
  costCurrencyCode: string = "USD";
  iconPath: string = "";

  isPestDivision: boolean = false;

  //Map the enum to arrays for dropdown options
  TvdValuePillars = Object.values(TvdValuePillar);
  TvdImpactCategories : TvdImpactCategory[] = Object.values(TvdImpactCategory);
  ValuePillarToImpactCategoryMapping = ValuePillarToImpactCategoryMapping;

  @Input() customTilesDict : Map<string, custTile> | undefined;
  @Input() filtersSelected : ContractModel | undefined;

  @Output() hideCustEntryTilePanel = new EventEmitter<string>();
  @Output() inputModelChange = new EventEmitter<string>();
  @Output() custTileSectionVisible= new EventEmitter<boolean>();
  @Output() newCustomTileAdded = new EventEmitter<custTile>();

  //frontend selectedItemsDict will have issue with any of the keys,
  //backend will throw error if there is a repeating title
  private get idAndTitleMap(): {[index:string]:string} {
    // TODO: Refactor to reuse PestTileMap and StandardTileMap from tvd-types.ts to avoid duplication (DRY principle)
    // For Pest division, only include Pest-specific entries plus custom-tile
    if (this.isPestDivision) {
      return {
        [eroiIds.NOCHARGEADDITIONALSERVICES]: "No Charge Additional Services",
        [eroiIds.GENERALCONTRACTORCOSTAVOIDANCE]: "General Contractor Cost Avoidance",
        [eroiIds.SETUPSERVICES]: "Set-up Services",
        [eroiIds.PESTPREVENTIONFINDINGS]: "Pest Prevention Findings",
        [eroiIds.ROOMNIGHTSSAVED]: "Room Nights Saved",
        [eroiIds.FURNITUREPRESERVATIONASSETS]: "Furniture Preservation (Assets)",
        [eroiIds.FURNITUREPRESERVATIONENERGY]: "Furniture Preservation (Energy)",
        "custom-tile": ""
      };
    }

    return {
      [eroiIds.WATER] : "Water",
      "energy": "Energy",
      "greenhouse-gases": "Greenhouse Gases",
      "plastic-waste": "Plastic Waste",
      "productivity": "Productivity",
      "assets": "Assets",
      "cardboard-waste": "Cardboard Waste",
      "employee-safety": "Employee Safety",
      "cost-avoidance": "Cost Avoidance",
      "portfolio-reduction": "Portfolio Reduction",
      "custom-tile": ""
    };
  }


  constructor(private _tvdService : TVDService,
    private mixpanelService : MixpanelService,
    private eroiMetricService : eroiService,
    private currencyUomService: CurrencyUomService
  ) { }

  // Getter methods to determine labels based on division
  get costLabel(): string {
    return this.isPestDivision ? 'Total TVD $ Value' : 'Cost';
  }

  get valueLabel(): string {
    return this.isPestDivision ? 'Total Number of Units' : 'Value';
  }

  ngOnInit(): void {
    if(this.filtersSelected) {
      this.costCurrencyCode = this.currencyUomService.getDisplayedCurrency();
      this.isPestDivision = this.filtersSelected?.division == 'Pest';
    }

    this.iconPath = this.eroiMetricService.mapImpactCategoryToIconPath(this.inputImpactCategory);
    this.currencySubscription = this.currencyUomService.displayedCurrency$.subscribe(() => {
      this.costCurrencyCode = this.currencyUomService.getDisplayedCurrency();
    });
  }

  ngOnDestroy(): void {
    this.currencySubscription?.unsubscribe();
  }

  clickSaveButton(): boolean {
    // Validate all inputs before proceeding
    const validationError = this.validateInputs();
    if (validationError) {
      window.alert(validationError);
      return false;
    }

    // Process the data
    const formattedCost = this.formatNumberString(this.inputCost);
    const formattedValue = this.formatNumberString(this.inputValue);

    // Create and save the custom tile
    const custTileObject = this.saveCustomTile(formattedCost, formattedValue);

    // Track event and emit signals to parent component
    this.trackCustomTileEvent("Saved", formattedCost, formattedValue);
    this.custTileSectionVisible.emit(true);
    this.hideCustEntryTilePanel.emit();
    this.newCustomTileAdded.emit(custTileObject);

    return true;
  }

  clickCloseLink() {
    this.hideCustEntryTilePanel.emit();
    this.trackCustomTileEvent("Cancelled", this.inputCost, this.inputValue);
  }

  clickClearButton() {
    this.inputTitle = "";
    this.inputCost = "";
    this.inputValue = "";
    this.inputUnit = "";
    this.inputValuePillar = "";
    this.inputImpactCategory = "";
    this.costCurrencyCode = "USD";
  }

  keyEvent(event: { keyCode: number; preventDefault: () => void; })
  {
    if(event.keyCode === 13) { //Enter Key
      event.preventDefault();
      this.clickSaveButton();
    }

    if (event.keyCode === 27) { // Escape key
      event.preventDefault();
      this.clickCloseLink();
    }
  }

  textChange(){
    // this.inputModelChange.emit(this.inputTitle);
  }

  // As user selects a Value Pillar, update the Impact Categories based on selected Value Pillar
  onValuePillarChange(): void {
    this.UpdateAvailableImpactCategoriesForValuePillar(this.inputValuePillar);
    this.inputImpactCategory = ''; // Reset selected Impact Category
  }

  // As user selects an Impact Category, update the Value Pillar selected based on selected Impact Category
  onImpactCategoryChange(): void {
    let valuePillar : TvdValuePillar | undefined = undefined;
    for (const [pillar, impactCategories] of this.ValuePillarToImpactCategoryMapping.entries()) {
      //foreach set of impact categories, if the selected impact category is in the set, return the value pillar
      if (impactCategories.includes(this.inputImpactCategory as TvdImpactCategory))
      {
        valuePillar = pillar;
        break;
      }
    }

    // Update tile icon based on impact category selection
    this.iconPath = this.eroiMetricService.mapImpactCategoryToIconPath(this.inputImpactCategory);

    // Update the selection of the value pillar dropdown
    this.inputValuePillar = valuePillar ?? "";

    //also update the available options for the Impact Categories for the correct Value Pillar
    this.UpdateAvailableImpactCategoriesForValuePillar(this.inputValuePillar);
  }

  // Update the Impact Categories based on selected Value Pillar
  private UpdateAvailableImpactCategoriesForValuePillar(valuePillar : string | TvdValuePillar){
    let validImpactCategories = this.ValuePillarToImpactCategoryMapping.get(valuePillar as TvdValuePillar);
    this.TvdImpactCategories = validImpactCategories ?? Object.values(TvdImpactCategory);
  }

  // Helper Methods
  private validateInputs(): string | null {
    if (!this.inputTitle) {
      return "Please enter a title for the new tile.";
    }

    if (!this.inputCost) {
      return "Please enter a cost for the new tile.";
    }

    if (!this.inputValue) {
      return "Please enter a value for the new tile.";
    }

    if (!this.inputUnit) {
      return "Please enter Units of Measurement for the new tile.";
    }

    if (this.inputTitle.includes("_")) {
      return "Title cannot include an underscore.";
    }

    if(!this.inputValuePillar){
      return "Please select a Value Pillar.";
    }

    if(!this.inputImpactCategory){
      return "Please select an Impact Category."
    }

    if (this.customTilesDict?.get(this.inputTitle)) {
      return "Duplicate title found, please enter a unique title.";
    }

    if (this.isDuplicatePredefinedTitle(this.inputTitle)) {
      return `Duplicate title id found (${this.inputTitle}), try adding a special character or suffix to ensure uniqueness.`;
    }

    if (!this.isNumericString(this.inputCost)) {
      return "The cost field may only contain numerical characters.";
    }

    if (!this.isNumericString(this.inputValue)) {
      return "The value field may only contain numerical characters.";
    }

    return null;
  }

  private isDuplicatePredefinedTitle(title: string): boolean {
    return Object.keys(this.idAndTitleMap).includes(title) ||
           Object.values(this.idAndTitleMap).includes(title);
  }

  private isNumericString(value: string): boolean {
    return /^[0-9,]+$/.test(value);
  }

  private formatNumberString(value: string): string {
    // Remove existing commas
    const numericValue = value.replace(/,/g, '');

    // Format with commas if long enough
    if (numericValue.length > 3) {
      return Number(numericValue).toLocaleString();
    }

    return numericValue;
  }

  private saveCustomTile(cost: string, value: string): custTile {
    const custTileObject: custTile = {
      title: this.inputTitle,
      cost: cost,
      metricValue: value,
      unit: this.inputUnit,
      valuePillar: this.inputValuePillar as TvdValuePillar,
      impactCategory: this.inputImpactCategory as TvdImpactCategory
    };

    if (this.customTilesDict) {
      this.customTilesDict.set(this.inputTitle, custTileObject);
      this._tvdService.updateCustomTileDict(this.customTilesDict);
    }

    //Automatically add the custom to the presentation payload.
    this._tvdService.addCustomMetricToPresentation(this.inputTitle);
    return custTileObject;
  }

  private trackCustomTileEvent(action: string, cost: string, value: string): void {
    this.mixpanelService.track('Add Custom Tile Clicked', {
      "countriesSelected": this.filtersSelected?.countries,
      "customerNumberSelected": this.filtersSelected?.accts,
      "H5s": this.filtersSelected?.topLevelContractGroup,
      "H7s": this.filtersSelected?.secondLevelContractFilter,
      "periodSelected": this.filtersSelected?.period,
      "Custom Date Range": this.filtersSelected?.dateRange,
      "regionSelected": this.filtersSelected?.regions,
      "templateSelected": this.filtersSelected?.presentationType ? this.filtersSelected.presentationType : 'All',
      "templateTranslationSelected": this.filtersSelected?.translation ? this.filtersSelected.translation : 'English',
      "Page Name": "Total Value Delivered",
      "Cost": cost,
      "Value": value,
      "Units of Measurement": this.inputUnit,
      "Title": this.inputTitle,
      "Value Pillar": this.inputValuePillar,
      "Impact Category": this.inputImpactCategory,
      "Custom Tile Added": action
    });
  }

}
